/* eslint-disable @typescript-eslint/explicit-function-return-type */
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Icon,
    Tooltip,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
} from '@mui/material';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';

import { SearchTextField } from 'app/components/form-controls/search-text-field';
import { RequireRole } from 'app/components/require-role';
import { GeneralConstants } from 'app/constants';
import { User } from 'app/model/user';
import { userService } from 'app/services/user.service';
import { RootState } from 'app/store';

import { CsvBuilder } from 'app/utils/csv-builder';
import { LocalDownloader } from 'app/utils/local-downloader';

import {
    deleteUser,
    listUsers,
    prepareCreateUser,
    prepareRoles,
    selectPossibleRoles,
    selectUser,
    selectUserList,
} from './user.slice';

interface ListProps extends RouteComponentProps {
    userList: User[];

    possibleRoles: Record<string, string>;

    listUsers: (search?: string) => void;

    prepareCreateUser: () => void;

    prepareRoles: () => void;

    selectUser: (user: User) => void;

    deleteUser: (user: User) => void;
}

interface ListState {
    open: boolean;
    deleting?: User;
}

class UserList extends React.Component<ListProps, ListState> {
    public constructor(props: ListProps) {
        super(props);

        this.state = {
            open: false,
        };
    }

    public componentDidMount() {
        this.props.prepareRoles();
        this.props.listUsers();
    }

    public dismissDialog() {
        this.setState({
            open: false,
            deleting: undefined,
        });
    }

    public describeRole(roles: string[]): string {
        const roleStr = roles
            .filter((role) => 'ROLE_USER' !== role)
            .map((role) => this.props.possibleRoles[role] || role)
            .join(', ');

        if (!roleStr) {
            return 'Basic User';
        }

        return roleStr;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public render() {
        return (
            <div className='pt-2'>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <SearchTextField onSearch={(val) => this.props.listUsers(val)} />
                    </Box>
                    <Box>
                        <Tooltip title='Download Users'>
                            <IconButton color='primary' size='small' onClick={() => this.exportUsers()}>
                                <Icon>file_download</Icon>
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box pl={2}>
                        <Button
                            to={`${this.props.match.path}/new`}
                            component={Link}
                            variant='contained'
                            onClick={() => {
                                this.props.prepareCreateUser();
                            }}
                        >
                            Create
                        </Button>
                    </Box>
                </Box>

                <List>
                    {this.props.userList.map((user) => (
                        <ListItem key={user.id}>
                            <ListItemText
                                primary={`${user.first} ${user.last} / ${user.username}
                                (${this.describeRole(user.roles)})`}
                                secondary={user.id}
                            />
                            <ListItemSecondaryAction>
                                <RequireRole roles={user.roles} all>
                                    {/* User must have at least the role of the target user to edit. */}
                                    <Link
                                        to={`${this.props.match.path}/${user.id}`}
                                        onClick={() => this.props.selectUser(user)}
                                    >
                                        <IconButton aria-label='edit' size='large'>
                                            <Icon color='primary'>edit</Icon>
                                        </IconButton>
                                    </Link>
                                    <IconButton
                                        aria-label='delete'
                                        onClick={() => {
                                            this.setState({ open: true, deleting: user });
                                        }}
                                        size='large'
                                    >
                                        <Icon color='secondary'>delete</Icon>
                                    </IconButton>
                                </RequireRole>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
                <Dialog open={this.state.open} onClose={() => this.dismissDialog()}>
                    <DialogTitle>Confirm Deactivation</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Deactivating a user removes their access and hides the record from this list. Are you sure
                            you want to deactivate this user?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => this.dismissDialog()}>Cancel</Button>
                        <Button
                            onClick={() => {
                                if (this.state.deleting) {
                                    this.props.deleteUser(this.state.deleting);
                                }
                                this.dismissDialog();
                            }}
                            color='primary'
                            autoFocus
                        >
                            Deactivate
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private exportUsers() {
        userService
            .listAll({ pageSize: GeneralConstants.PAGE_SIZE_HUGE, extraParams: { complete: true } })
            .subscribe((users) => {
                const builder = new CsvBuilder();
                builder.addRow(['First Name', 'Last Name', 'Username', 'Roles']);

                if (users && 0 < users.length) {
                    users.forEach((user) => {
                        if ('root' !== user.username && 'system' !== user.username) {
                            builder.addRow([user.first, user.last, user.username, this.describeRole(user.roles)]);
                        }
                    });
                }

                const filename = 'users.csv';

                LocalDownloader.downloadBlob(builder.toBlob(), filename);
            });
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: RootState) => ({
    possibleRoles: selectPossibleRoles(state),
    userList: selectUserList(state),
});

const connected = connect(mapStateToProps, { listUsers, selectUser, prepareCreateUser, deleteUser, prepareRoles })(
    withRouter(UserList),
);

export { connected as UserList };
